import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SendIcon from '@mui/icons-material/Send';
import {
  IntelPacket,
  sentinelAsk,
} from '@signals/app-shell/services/intelPacketApi';

type ChatRole = 'user' | 'sentinel';

interface ChatMessage {
  role: ChatRole;
  content: string;
}

interface PromptCategory {
  category: string;
  prompts: Array<{ label: string; prompt: string }>;
}

interface IntelPacketSentinelProps {
  packet: IntelPacket;
  busy: 'insights' | 'assessment' | 'ask' | null;
  onBusy: (next: 'insights' | 'assessment' | 'ask' | null) => void;
  onError: (message: string | null) => void;
}

function packetPrompts(packet: IntelPacket): PromptCategory[] {
  const subject = packet.primary_subject_type
    ? packet.primary_subject_type.replace(/_/g, ' ')
    : 'this subject';
  const name = packet.name;
  return [
    {
      category: 'Picture',
      prompts: [
        { label: 'Current picture', prompt: `What is the current intelligence picture on ${name}?` },
        { label: 'What we know', prompt: `What do we actually know about the ${subject} so far?` },
        { label: 'What to brief now', prompt: 'What should I brief right now, and what is still unknown?' },
      ],
    },
    {
      category: 'Collection',
      prompts: [
        { label: 'What to collect next', prompt: 'What should I collect next on this packet?' },
        { label: 'Collection gaps', prompt: 'Where are the collection gaps?' },
        { label: 'Monitor payoff', prompt: 'Which monitors are paying off, and which look quiet?' },
      ],
    },
    {
      category: 'Threat and RF',
      prompts: [
        { label: 'DFP and RF activity', prompt: 'Summarize DFP and RF activity on this packet.' },
        { label: 'Co-travelers and follows', prompt: 'Are there co-traveler, follow, or watchlist hits I should care about?' },
        { label: 'Worth elevating', prompt: 'Any drone, BLE flood, or GNSS interference worth elevating?' },
      ],
    },
    {
      category: 'Pattern of Life',
      prompts: [
        { label: 'What stands out', prompt: 'What Pattern of Life stands out for the primary subject?' },
        { label: 'Habitual vs new', prompt: 'Which places or times look habitual versus new?' },
        { label: 'Off-pattern', prompt: 'Is anything off-pattern enough to reopen analysis?' },
      ],
    },
    {
      category: 'Product',
      prompts: [
        { label: 'Executive summary', prompt: 'Draft an executive summary from the file only.' },
        { label: 'Publishable findings', prompt: 'List key findings I can publish, each tied to activity in the packet.' },
        { label: '24-hour update', prompt: 'What would a 24-hour update say that the last assessment did not?' },
      ],
    },
  ];
}

const IntelPacketSentinel: React.FC<IntelPacketSentinelProps> = ({
  packet,
  busy,
  onBusy,
  onError,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [question, setQuestion] = useState('');
  const [promptAnchor, setPromptAnchor] = useState<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const categories = useMemo(() => packetPrompts(packet), [packet]);
  const thinking = busy === 'ask';

  const ask = async (text: string) => {
    const q = text.trim();
    if (!q || busy) return;
    setQuestion('');
    setMessages((prev) => [...prev, { role: 'user', content: q }]);
    onBusy('ask');
    onError(null);
    try {
      const res = await sentinelAsk(packet.id, q);
      setMessages((prev) => [...prev, { role: 'sentinel', content: res.answer || 'No response.' }]);
      requestAnimationFrame(() => {
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
      });
    } catch (e: any) {
      onError(e?.response?.data?.detail || e?.message || 'Sentinel ask failed');
    } finally {
      onBusy(null);
    }
  };

  const transcript = (maxHeight: number | string) => (
    <Box ref={listRef} sx={{ maxHeight, overflow: 'auto', mb: messages.length ? 1 : 0 }}>
      {messages.map((m, i) => (
        <Box key={`${m.role}-${i}`} sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: m.role === 'user' ? 'text.secondary' : '#f5b942' }}
          >
            {m.role === 'user' ? 'You' : 'Sentinel'}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {m.content}
          </Typography>
        </Box>
      ))}
      {thinking && (
        <Typography variant="caption" color="text.secondary">Sentinel is reading the file…</Typography>
      )}
    </Box>
  );

  const input = (autoFocus?: boolean) => (
    <TextField
      fullWidth
      size="small"
      autoFocus={autoFocus}
      multiline
      maxRows={4}
      placeholder="Ask this packet, or describe the report you want…"
      value={question}
      onChange={(e) => setQuestion(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          ask(question);
        }
      }}
      InputProps={{
        sx: { fontSize: '0.82rem', pr: 0.5 },
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title="Suggested questions">
              <IconButton size="small" onClick={(e) => setPromptAnchor(e.currentTarget)} sx={{ color: '#f5b942' }}>
                <LightbulbOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              disabled={!!busy || !question.trim()}
              onClick={() => ask(question)}
              sx={{ color: question.trim() ? '#f5b942' : undefined }}
            >
              {thinking ? <CircularProgress size={14} /> : <SendIcon fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, flex: 1 }}>Ask Sentinel</Typography>
        {messages.length > 0 && (
          <Typography
            variant="caption"
            onClick={() => setMessages([])}
            sx={{ cursor: 'pointer', color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
          >
            Clear
          </Typography>
        )}
        <Tooltip title="Open full view">
          <IconButton size="small" onClick={() => setExpanded(true)}>
            <OpenInFullIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
      {transcript(220)}
      {input()}
      {messages.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Grounded in this packet only — roster, activity, insights, evidence titles.
        </Typography>
      )}

      <Menu
        anchorEl={promptAnchor}
        open={!!promptAnchor}
        onClose={() => setPromptAnchor(null)}
        PaperProps={{ sx: { width: 300, maxHeight: 420 } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 0.75 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>Suggested questions</Typography>
          <Typography variant="caption" color="text.secondary">Click to ask the packet file</Typography>
        </Box>
        <Divider />
        {categories.flatMap((cat) => [
          <Typography
            key={`h-${cat.category}`}
            variant="caption"
            sx={{ display: 'block', px: 2, pt: 1, pb: 0.25, fontWeight: 700, color: '#f5b942' }}
          >
            {cat.category}
          </Typography>,
          ...cat.prompts.map((p) => (
            <MenuItem
              key={p.label}
              onClick={() => { setPromptAnchor(null); ask(p.prompt); }}
              sx={{ py: 0.4 }}
            >
              <ListItemText primaryTypographyProps={{ variant: 'body2', fontSize: '0.8rem' }} primary={p.label} />
            </MenuItem>
          )),
        ])}
      </Menu>

      <Dialog open={expanded} onClose={() => setExpanded(false)} maxWidth="md" fullWidth>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, flex: 1 }}>
            Ask Sentinel · {packet.name}
          </Typography>
          <IconButton size="small" onClick={() => setExpanded(false)}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <Divider />
        <DialogContent sx={{ pt: 1.5 }}>
          {messages.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Answers stay grounded in what collectors have already filed for this packet.
            </Typography>
          )}
          {transcript('55vh')}
          {input(true)}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default IntelPacketSentinel;
